/**
 * Auth constants for the Worker ↔ Convex trust boundary — the rules both
 * sides of it state, imported from one place so the two copies of a rule
 * cannot come to different conclusions about its number or its pattern.
 *
 * Zod contexts validate reader IDs through `ReaderIdSchema` in
 * `./readerId.ts`; Convex code imports `UUID_REGEX` from here directly.
 *
 * MUST stay pure TypeScript: no `node:` imports, no framework imports,
 * no env access. Convex bundles this file into its deployment, and the
 * broker Worker — which runs without `nodejs_compat` — bundles it into
 * its script, so anything either runtime lacks breaks a bundle rather
 * than a call.
 */

/**
 * RFC 4122 UUID (versions 1-5). For validating reader IDs on both sides
 * of that boundary.
 */
export const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Any 8-4-4-4-12 hex string, version and variant unchecked. For ids that are
 * only grouping or lookup keys, where refusing a v7 or a nil UUID would drop
 * a well-formed request; anything that authorizes uses UUID_REGEX.
 */
export const UUID_SHAPE_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Maximum stored length of a reader nickname. Enforced wherever a
 * nickname is accepted or derived (OAuth callbacks, roster imports,
 * Convex reader creation).
 */
export const NICKNAME_MAX_LENGTH = 50;

/**
 * The characters a reader nickname may be made of: letters and digits in
 * any script, whitespace, hyphens and apostrophes. Verbatim from the
 * reader app's `nickname-validation.ts`, where the same pattern gives the
 * dialog its "Letters, numbers, spaces, hyphens, and apostrophes only"
 * message.
 *
 * A nickname is a PUBLIC display name — it appears on shared classroom
 * devices and in a roster — so the rule is protective rather than
 * cosmetic, and it is stated here beside the cap for the same reason the
 * cap is: both `normalizeNickname` copies apply it, so a name that is
 * illegal through one door cannot be stored through the other. At the
 * plane and at the broker a failing name collapses to `undefined`, the
 * "nothing worth storing" the two functions already apply to whitespace;
 * the dialog is where a person is TOLD, and it imports this same pattern.
 */
export const ALLOWED_NICKNAME_PATTERN = /^[\p{L}\p{N}\s'-]+$/u;

/**
 * The nickname rule applied end to end: trim, compose to NFC, cap at
 * {@link NICKNAME_MAX_LENGTH}, and require {@link ALLOWED_NICKNAME_PATTERN}
 * — or `undefined` when nothing worth storing is left.
 *
 * ONE FUNCTION, called by both `normalizeNickname` copies (the identity
 * plane's and the broker's), so the two sides of the boundary cannot drift
 * on the ORDER of the steps the way they once drifted on the number. Each
 * copy keeps its own null-handling and its own header; the sequence lives
 * here.
 *
 * NFC BEFORE THE PATTERN, and the reason is `\p{L}`: it matches letters,
 * not combining marks (`\p{M}`), so a name typed in decomposed form —
 * "Zoë" as `Z o e ◌̈`, which is what some keyboards and every macOS file
 * name produce — fails the class on the mark alone and would be dropped
 * without a word. Composing first makes the same name pass in either form,
 * and it makes the STORED value stable: two children who typed "José" on
 * different devices get one spelling in the roster, not two that compare
 * unequal. A display name has no claim to verbatim code units, so NFC is
 * the right normalization rather than widening the class with `\p{M}`
 * (which would admit a lone mark and store the decomposed form as typed).
 * The cap is applied after composition so it counts the name a person
 * sees, and the pattern is judged on the capped candidate so whatever is
 * stored satisfies it.
 */
export function normalizeNicknameCandidate(raw: string): string | undefined {
  const candidate = raw.trim().normalize("NFC").slice(0, NICKNAME_MAX_LENGTH);
  return ALLOWED_NICKNAME_PATTERN.test(candidate) ? candidate : undefined;
}
